import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-eb484a46"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "text-center" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", { class: "mt-4" }, "Validating Merchant...", -1))
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "text-lightGray" }, " Please wait while we verify the merchant information ", -1))
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", { class: "text-center bold text-lg mb-2 mt-2 font-weight-bold" }, " Provide Your Email to Continue ", -1))
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "text-center text-lightGray font-weight-bold text-lg mb-2 mt-2" }, " Enter your email address to create your account ", -1))
const _hoisted_6 = { class: "justify-center mb-10 mt-6" }
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("br", null, null, -1))
const _hoisted_8 = {
  class: "justify-center mt-12",
  style: {"display":"flex"}
}
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("br", null, null, -1))
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h5", { class: "text-lightGray text-center" }, [
  /*#__PURE__*/_createTextVNode(" By creating an account, I agree to the Binkey's "),
  /*#__PURE__*/_createElementVNode("a", {
    class: "text-tertiary",
    href: "https://www.joinbinkey.com/terms-and-conditions"
  }, " Terms of Service"),
  /*#__PURE__*/_createTextVNode(" and confirm that I have read Binkey's "),
  /*#__PURE__*/_createElementVNode("a", {
    class: "text-tertiary",
    href: "https://www.joinbinkey.com/privacy-policy"
  }, " Privacy Policy"),
  /*#__PURE__*/_createTextVNode(" and accept the Store Terms. ")
], -1))

import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Auth } from "aws-amplify";
import BinkeyLogo from "@/components/common/BinkeyLogo.vue";
import { useMerchantStore } from "@/stores/Merchant";
import { useSessionStore } from "@/stores/Session";


export default /*#__PURE__*/_defineComponent({
  __name: 'VisionMerchant',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const merchantStore = useMerchantStore();
const sessionStore = useSessionStore();

// Reactive state
const email = ref("");
const loading = ref(false);
const validForm = ref(false);
const hasError = ref(false);
const errorMessage = ref("");
const sheetWidth = ref(calculateWidth());
const isValidatingMerchant = ref(true);

// Email validation rules
const emailRules = [
  (v: string) => !!v || "Email is required",
  (v: string) => /.+@.+\..+/.test(v) || "Email must be valid",
];

// Computed properties
const canContinue = computed(() => {
  return validForm.value && email.value.length > 0 && !loading.value;
});

// Methods
function calculateWidth() {
  if (typeof window === "undefined") return "60%";

  const screenWidth = window.innerWidth;

  if (screenWidth <= 700) {
    return "90%";
  } else if (screenWidth <= 1000) {
    return "80%";
  } else if (screenWidth <= 1300) {
    return "70%";
  } else {
    return "60%";
  }
}

function handleResize() {
  sheetWidth.value = calculateWidth();
}

async function validateMerchant() {
  const merchantId = route.params.id as string;

  if (!merchantId) {
    console.error("No merchant ID provided");
    router.push("/404");
    return;
  }

  try {
    const response = await merchantStore.validateMerchant(merchantId);
    const merchantData = response.data;

    if (!merchantData.exists) {
      console.log("Merchant does not exist, redirecting to not found");
      router.push("/404");
      return;
    }

    console.log("Merchant validation successful:", merchantData);
  } catch (error) {
    console.error("Error validating merchant:", error);
    router.push("/404");
    return;
  } finally {
    isValidatingMerchant.value = false;
  }
}

async function logoutCurrentUser() {
  try {
    await Auth.signOut();
    console.log("User logged out successfully");
  } catch (error) {
    console.log("No user was signed in or error signing out:", error);
  }
}

async function createCognitoAccount(emailAddress: string) {
  try {
    const tempPassword = generateTempPassword();

    const result = await Auth.signUp({
      username: crypto.randomUUID(),
      password: tempPassword,
      attributes: {
        email: emailAddress.toLowerCase(),
      },
    });

    console.log("Account creation result:", result);
    return;
  } catch (error: any) {
    console.error("Error creating account:", error);

    if (error.message.toLowerCase().includes("email already exists")) {
      return;
    } else if (error.code === "UsernameExistsException") {
      throw new Error("An account with this email already exists");
    } else if (error.code === "InvalidParameterException") {
      throw new Error("Please enter a valid email address");
    } else {
      throw new Error("Failed to create account. Please try again.");
    }
  }
}

function generateTempPassword(): string {
  // Generate a temporary password that meets Cognito requirements
  const chars =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
  let password = "";

  // Ensure at least one of each required character type
  password += "A"; // uppercase
  password += "a"; // lowercase
  password += "1"; // number
  password += "!"; // special char

  // Add random characters to reach minimum length
  for (let i = 4; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  // Shuffle the password
  return password
    .split("")
    .sort(() => 0.5 - Math.random())
    .join("");
}

async function onSubmit() {
  if (!validForm.value) return;

  loading.value = true;
  hasError.value = false;
  errorMessage.value = "";

  try {
    await createCognitoAccount(email.value);
    console.log("Account created successfully for:", email.value);

    // Get merchantId from route params
    const merchantId = route.params.id as string;

    // Generate session with merchantId and email
    const sessionResponse = await sessionStore.generateSession(
      merchantId,
      email.value
    );

    console.log("Session generation response:", sessionResponse);
    window.location.href = sessionResponse.data.link;
  } catch (error: any) {
    hasError.value = true;
    errorMessage.value =
      error.message || "An error occurred. Please try again.";
  } finally {
    loading.value = false;
  }
}

// Lifecycle hooks
onMounted(async () => {
  window.addEventListener("resize", handleResize);

  // First validate the merchant exists
  await validateMerchant();

  // Only logout if merchant validation passed
  if (!isValidatingMerchant.value) {
    await logoutCurrentUser();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_sheet = _resolveComponent("v-sheet")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(BinkeyLogo),
    _createVNode(_component_v_sheet, {
      class: "mx-auto my-auto",
      width: sheetWidth.value,
      color: "#f0f0f0"
    }, {
      default: _withCtx(() => [
        (isValidatingMerchant.value)
          ? (_openBlock(), _createBlock(_component_v_card, {
              key: 0,
              class: "my-14 pa-8 card-container"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_1, [
                  _createVNode(_component_v_progress_circular, {
                    indeterminate: "",
                    color: "primary",
                    size: "64"
                  }),
                  _hoisted_2,
                  _hoisted_3
                ])
              ]),
              _: 1
            }))
          : (_openBlock(), _createBlock(_component_v_card, {
              key: 1,
              class: "my-14 pa-8 card-container"
            }, {
              default: _withCtx(() => [
                _hoisted_4,
                _hoisted_5,
                _createVNode(_component_v_row, {
                  justify: "center",
                  class: "mt-6"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, {
                      cols: "12",
                      sm: "8",
                      md: "6"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_form, {
                          modelValue: validForm.value,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((validForm).value = $event)),
                          onSubmit: _withModifiers(onSubmit, ["prevent"]),
                          class: "form-container"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_6, [
                              _createVNode(_component_v_text_field, {
                                modelValue: email.value,
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((email).value = $event)),
                                "data-testid": "email-input",
                                label: "Email Address",
                                type: "email",
                                variant: "outlined",
                                rules: emailRules,
                                error: hasError.value,
                                "error-messages": errorMessage.value,
                                class: "email-input",
                                "bg-color": "#FFF5F9",
                                required: ""
                              }, null, 8, ["modelValue", "error", "error-messages"])
                            ]),
                            _hoisted_7,
                            _createElementVNode("div", _hoisted_8, [
                              _createVNode(_component_v_btn, {
                                disabled: !canContinue.value,
                                loading: loading.value,
                                color: "primary",
                                type: "submit",
                                class: "rounded-lg text-capitalize",
                                width: "280",
                                height: "45",
                                variant: "elevated",
                                style: {"font-weight":"100","font-size":"16px"}
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(" Continue "),
                                  _createVNode(_component_v_icon, {
                                    size: "large",
                                    class: "ml-2 w-2"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode("mdi-arrow-right")
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }, 8, ["disabled", "loading"])
                            ]),
                            _hoisted_9
                          ]),
                          _: 1
                        }, 8, ["modelValue", "onSubmit"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_card, {
                  variant: "flat",
                  class: "terms-container d-flex pa-4 mx-auto",
                  width: sheetWidth.value
                }, {
                  default: _withCtx(() => [
                    _hoisted_10
                  ]),
                  _: 1
                }, 8, ["width"])
              ]),
              _: 1
            }))
      ]),
      _: 1
    }, 8, ["width"])
  ], 64))
}
}

})